import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse } from '@/shared/types/api';
import { getDeviceFingerprint } from '@/utils/deviceFingerprint';
import { BACKEND_CONFIG } from '@/config/backendConfig';

class ApiClient {
  private instance: AxiosInstance;
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    // For multi-tenancy: use current hostname with configured port
    const getApiBaseUrl = () => {
      const hostname = window.location.hostname;
      const apiPort = BACKEND_CONFIG.apiPort; // Use configured port (8000 for Django, 8001 for FastAPI)

      // Debug logging for production troubleshooting
      console.log('🔍 API URL Construction Debug:', {
        hostname,
        backend: BACKEND_CONFIG.backendName,
        port: apiPort,
        VITE_API_URL: import.meta.env.VITE_API_URL,
        protocol: window.location.protocol,
        allEnvVars: import.meta.env,
      });

      // If on localhost subdomain (e.g., anandlab.localhost), always use configured backend
      // This allows switching between Django and FastAPI during development
      if (hostname.includes('.localhost') || hostname === 'localhost') {
        // Both FastAPI and Django use /api prefix - no difference needed
        const url = `http://${hostname}:${apiPort}`;
        console.log(`✅ Using ${BACKEND_CONFIG.backendName} on localhost:`, url);
        return url;
      }

      // For production/deployed environments, use env variable
      // This allows frontend (Vercel) to connect to backend (Railway)
      const apiUrl = import.meta.env.VITE_API_URL;

      if (apiUrl) {
        console.log('✅ Using VITE_API_URL from environment:', apiUrl);
        return apiUrl;
      }

      // Fallback only for development - should never be used in production
      const fallbackUrl = `${window.location.protocol}//${hostname}:${apiPort}`;
      console.warn('⚠️ VITE_API_URL not found! Falling back to:', fallbackUrl);
      return fallbackUrl;
    };

    this.instance = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load tokens from localStorage
    this.loadTokens();

    // Request interceptor
    this.instance.interceptors.request.use(
      async (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        // Add device fingerprint to all requests
        const fingerprint = await getDeviceFingerprint();
        config.headers['X-Device-Fingerprint'] = fingerprint;

        // Add tenant header based on backend configuration
        // FastAPI: X-Tenant with schema name (e.g., "adlabs")
        // Django: X-Tenant-Domain with full hostname
        const tenantHeader = BACKEND_CONFIG.getTenantHeader();
        const tenantValue = BACKEND_CONFIG.getTenantValue();
        config.headers[tenantHeader] = tenantValue;

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't intercept 401 errors from login endpoints - let them propagate to show error messages
        const isLoginEndpoint = originalRequest.url?.includes('/auth/login') ||
                                originalRequest.url?.includes('/referral-portal/login');

        if (error.response?.status === 401 && !originalRequest._retry && !isLoginEndpoint) {
          originalRequest._retry = true;

          try {
            await this.refreshAccessToken();
            return this.instance(originalRequest);
          } catch (refreshError) {
            this.clearTokens();
            // Redirect to appropriate login page based on user type
            const loginUrl = this.getLoginUrl();
            window.location.href = loginUrl;
            return Promise.reject(refreshError);
          }
        }

        // Handle 401 errors that already went through retry or don't need retry
        // but skip login endpoints to allow error messages to be shown
        if (error.response?.status === 401 && !isLoginEndpoint) {
          this.clearTokens();
          const loginUrl = this.getLoginUrl();
          window.location.href = loginUrl;
        }

        return Promise.reject(error);
      }
    );
  }

  private loadTokens(): void {
    this.accessToken = sessionStorage.getItem('access_token');
    this.refreshToken = sessionStorage.getItem('refresh_token');
  }

  private saveTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    sessionStorage.setItem('access_token', accessToken);
    sessionStorage.setItem('refresh_token', refreshToken);
  }

  private clearTokens(): void {
    this.accessToken = null;
    this.refreshToken = null;
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  private getLoginUrl(): string {
    // Check user type from localStorage to determine login page
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.user_type === 'referral' || user.user_type === 'referral_team') {
          return '/referral-portal/login';
        }
      } catch (error) {
        // If parsing fails, default to staff login
      }
    }

    // Check current URL path to determine context
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/referral-portal')) {
      return '/referral-portal/login';
    }

    // Default to staff login
    return '/login';
  }

  private async refreshAccessToken(): Promise<void> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    // Use the same base URL logic for refresh
    const baseURL = this.instance.defaults.baseURL;
    const response = await axios.post(`${baseURL}/auth/refresh`, {
      refresh_token: this.refreshToken,
    });

    const { access_token, refresh_token } = response.data;
    this.accessToken = access_token;
    this.refreshToken = refresh_token;
    sessionStorage.setItem('access_token', access_token);
    sessionStorage.setItem('refresh_token', refresh_token);
  }

  public setTokens(accessToken: string, refreshToken: string): void {
    this.saveTokens(accessToken, refreshToken);
  }

  public clearAuth(): void {
    this.clearTokens();
  }

  public isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  // HTTP Methods
  public async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.get(url, config);
  }

  public async post<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.post(url, data, config);
  }

  public async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.put(url, data, config);
  }

  public async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.patch(url, data, config);
  }

  public async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.instance.delete(url, config);
  }

  // Utility method to handle API responses
  public static handleResponse<T>(response: AxiosResponse<ApiResponse<T>>): T {
    if (response.data.success) {
      return response.data.data as T;
    }
    throw new Error(response.data.message || 'API request failed');
  }

  // Error handling utility
  public static getErrorMessage(error: any): string {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      const firstError = Object.values(errors)[0] as string[];
      return firstError[0] || 'Validation error occurred';
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  }

  // Get validation errors
  public static getValidationErrors(error: any): Record<string, string[]> | null {
    return error.response?.data?.errors || null;
  }
}

export const apiClient = new ApiClient();
export default apiClient;
export { ApiClient };

// Department API
export const departmentApi = {
  list: (params?: { is_active?: boolean; search?: string }) => {
    // FastAPI: /api/internal/facilities/departments/
    // Django: /facilities/departments/
    const endpoint = BACKEND_CONFIG.isFastAPI ? '/api/internal/facilities/departments/' : '/facilities/departments/';
    return apiClient.get(endpoint, { params });
  },

  get: (departmentId: string) => {
    // FastAPI: /api/internal/facilities/departments/{id}
    // Django: /facilities/departments/{id}
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/facilities/departments/${departmentId}`
      : `/facilities/departments/${departmentId}`;
    return apiClient.get(endpoint);
  },

  create: (data: any) => {
    // FastAPI: POST /api/internal/facilities/departments
    // Django: POST /facilities/departments/create/
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/facilities/departments'
      : '/facilities/departments/create/';
    return apiClient.post(endpoint, data);
  },

  update: (departmentId: string, data: any) => {
    // FastAPI: PATCH /api/internal/facilities/departments/{id}
    // Django: PUT /facilities/departments/{id}/update/
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/facilities/departments/${departmentId}`
      : `/facilities/departments/${departmentId}/update/`;
    const method = BACKEND_CONFIG.isFastAPI ? apiClient.patch : apiClient.put;
    return method.call(apiClient, endpoint, data);
  },

  delete: (departmentId: string) => {
    // FastAPI: DELETE /api/internal/facilities/departments/{id}
    // Django: DELETE /facilities/departments/{id}/delete/
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/facilities/departments/${departmentId}`
      : `/facilities/departments/${departmentId}/delete/`;
    return apiClient.delete(endpoint);
  },
};

// Room API
export const roomApi = {
  list: (params?: { department_id?: string; is_active?: boolean; search?: string }) => {
    // FastAPI: /api/internal/facilities/rooms/
    // Django: /facilities/rooms/
    const endpoint = BACKEND_CONFIG.isFastAPI ? '/api/internal/facilities/rooms/' : '/facilities/rooms/';
    return apiClient.get(endpoint, { params });
  },

  get: (roomId: string) => {
    // FastAPI: /api/internal/facilities/rooms/{id}
    // Django: /facilities/rooms/{id}
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/facilities/rooms/${roomId}`
      : `/facilities/rooms/${roomId}`;
    return apiClient.get(endpoint);
  },

  create: (data: any) => {
    // FastAPI: POST /api/internal/facilities/rooms
    // Django: POST /facilities/rooms/create/
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/facilities/rooms'
      : '/facilities/rooms/create/';
    return apiClient.post(endpoint, data);
  },

  update: (roomId: string, data: any) => {
    // FastAPI: PATCH /api/internal/facilities/rooms/{id}
    // Django: PUT /facilities/rooms/{id}/update/
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/facilities/rooms/${roomId}`
      : `/facilities/rooms/${roomId}/update/`;
    const method = BACKEND_CONFIG.isFastAPI ? apiClient.patch : apiClient.put;
    return method.call(apiClient, endpoint, data);
  },

  delete: (roomId: string) => {
    // FastAPI: DELETE /api/internal/facilities/rooms/{id}
    // Django: DELETE /facilities/rooms/{id}/delete/
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/facilities/rooms/${roomId}`
      : `/facilities/rooms/${roomId}/delete/`;
    return apiClient.delete(endpoint);
  },
};

// Test Category API
export const testCategoryApi = {
  list: (params?: { is_active?: boolean; search?: string; with_test_counts?: boolean }) => {
    // FastAPI: /api/internal/test-categories
    // Django: /test-categories/
    const endpoint = BACKEND_CONFIG.isFastAPI ? '/api/internal/test-categories' : '/test-categories/';
    return apiClient.get(endpoint, { params });
  },

  get: (categoryId: string) => {
    // FastAPI: /api/internal/test-categories/{id}
    // Django: /test-categories/{id}
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/test-categories/${categoryId}`
      : `/test-categories/${categoryId}`;
    return apiClient.get(endpoint);
  },

  create: (data: any) => {
    // FastAPI: POST /api/internal/test-categories
    // Django: POST /test-categories/create
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/test-categories'
      : '/test-categories/create';
    return apiClient.post(endpoint, data);
  },

  update: (categoryId: string, data: any) => {
    // FastAPI: PATCH /api/internal/test-categories/{id}
    // Django: PUT /test-categories/{id}/update
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/test-categories/${categoryId}`
      : `/test-categories/${categoryId}/update`;
    const method = BACKEND_CONFIG.isFastAPI ? apiClient.patch : apiClient.put;
    return method.call(apiClient, endpoint, data);
  },

  delete: (categoryId: string) => {
    // FastAPI and Django: DELETE /test-categories/{id}/delete
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/test-categories/${categoryId}`
      : `/test-categories/${categoryId}/delete`;
    return apiClient.delete(endpoint);
  },
};

// Test API
export const testApi = {
  list: (params?: { is_active?: boolean; category_id?: string; department_id?: string; search?: string }) => {
    // FastAPI: /api/internal/tests
    // Django: /tests/
    const endpoint = BACKEND_CONFIG.isFastAPI ? '/api/internal/tests' : '/tests/';
    return apiClient.get(endpoint, { params });
  },

  get: (testId: string) => {
    // FastAPI: /api/internal/tests/{id}
    // Django: /tests/{id}
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/tests/${testId}`
      : `/tests/${testId}`;
    return apiClient.get(endpoint);
  },

  create: (data: any) => {
    // FastAPI: POST /api/internal/tests
    // Django: POST /tests/create
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/tests'
      : '/tests/create';
    return apiClient.post(endpoint, data);
  },

  update: (testId: string, data: any) => {
    // FastAPI: PATCH /api/internal/tests/{id}
    // Django: PUT /tests/{id}/update
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/tests/${testId}`
      : `/tests/${testId}/update`;
    const method = BACKEND_CONFIG.isFastAPI ? apiClient.patch : apiClient.put;
    return method.call(apiClient, endpoint, data);
  },

  delete: (testId: string) => {
    // FastAPI: DELETE /api/internal/tests/{id}
    // Django: DELETE /tests/{id}/delete
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/tests/${testId}`
      : `/tests/${testId}/delete`;
    return apiClient.delete(endpoint);
  },

  // Get tests by category endpoint
  byCategory: (params?: { category_id?: string }) => {
    // FastAPI: /api/internal/tests/by-category
    // Django: Uses same /tests/ endpoint with category filter
    if (BACKEND_CONFIG.isFastAPI) {
      return apiClient.get('/api/internal/tests/by-category', { params });
    } else {
      return apiClient.get('/tests/', { params });
    }
  },

  // Get available tests (public endpoint)
  available: () => {
    // FastAPI: /api/internal/tests/available
    // Django: /test-categories/ (returns different format)
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/tests/available'
      : '/test-categories/';
    return apiClient.get(endpoint);
  },
};

// Referral Management API
export const referralManagementApi = {
  // List all referral sources with rates
  listSources: () => {
    // FastAPI: /api/internal/referral-sources
    // Django: /referral-management
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/referral-sources'
      : '/referral-management';
    return apiClient.get(endpoint);
  },

  // Get active referral sources (for dropdowns/selectors)
  getActiveSources: (sourceType?: string) => {
    // FastAPI: /api/internal/referral-sources/active
    // Django: /referrals/active
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/referral-sources/active'
      : '/referrals/active';
    const params = sourceType ? { source_type: sourceType } : undefined;
    return apiClient.get(endpoint, { params });
  },

  // Get single referral source
  getSource: (referralSourceId: string) => {
    // FastAPI: /api/internal/referral-sources/{id}
    // Django: Not implemented in Django
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/referral-sources/${referralSourceId}`
      : `/referral-management/${referralSourceId}`;
    return apiClient.get(endpoint);
  },

  // Create referral source
  createSource: (data: any) => {
    // FastAPI: POST /api/internal/referral-sources
    // Django: POST /referral-management/create
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/referral-sources'
      : '/referral-management/create';
    return apiClient.post(endpoint, data);
  },

  // Update referral source
  updateSource: (referralSourceId: string, data: any) => {
    // FastAPI: PUT /api/internal/referral-sources/{id}
    // Django: PUT /referral-management/{id}/update
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/referral-sources/${referralSourceId}`
      : `/referral-management/${referralSourceId}/update`;
    return apiClient.put(endpoint, data);
  },

  // List test categories for rate management
  listTestCategories: () => {
    // FastAPI: Use existing test-categories endpoint
    // Django: /referral-management/test-categories
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/test-categories'
      : '/referral-management/test-categories';
    return apiClient.get(endpoint);
  },

  // Bulk update commission rates
  bulkUpdateCommissionRates: (data: {
    test_category_id: string;
    commission_type: string;
    commission_rate: number;
  }) => {
    // FastAPI: POST /api/internal/commission-rates/bulk-update
    // Django: POST /referral-management/commission-rates/bulk-update
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/commission-rates/bulk-update'
      : '/referral-management/commission-rates/bulk-update';
    return apiClient.post(endpoint, data);
  },

  // Bulk update discount rates
  bulkUpdateDiscountRates: (data: {
    test_category_id: string;
    discount_type: string;
    discount_rate: number;
  }) => {
    // FastAPI: POST /api/internal/discount-rates/bulk-update
    // Django: POST /referral-management/discount-rates/bulk-update
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/discount-rates/bulk-update'
      : '/referral-management/discount-rates/bulk-update';
    return apiClient.post(endpoint, data);
  },

  // Update individual commission rate
  updateIndividualCommissionRate: (referralSourceId: string, data: {
    test_category_id: string;
    commission_type: string;
    commission_rate: number;
    is_active?: boolean;
  }) => {
    // FastAPI: PUT /api/internal/commission-rates/{source_id}/individual
    // Django: PUT /referral-management/{source_id}/commission-rate/update
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/commission-rates/${referralSourceId}/individual`
      : `/referral-management/${referralSourceId}/commission-rate/update`;
    return apiClient.put(endpoint, data);
  },

  // Update individual discount rate
  updateIndividualDiscountRate: (referralSourceId: string, data: {
    test_category_id: string;
    discount_type: string;
    discount_rate: number;
    is_active?: boolean;
  }) => {
    // FastAPI: PUT /api/internal/discount-rates/{source_id}/individual
    // Django: PUT /referral-management/{source_id}/discount-rate/update
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/discount-rates/${referralSourceId}/individual`
      : `/referral-management/${referralSourceId}/discount-rate/update`;
    return apiClient.put(endpoint, data);
  },
};

// Patient API
export const patientApi = {
  // Search patients
  search: (params?: {
    phone?: string;
    name?: string;
    email?: string;
    patient_id?: string;
    page?: number;
    limit?: number;
  }) => {
    // FastAPI: GET /api/internal/patients with query params
    // Django: GET /patients/ with query params
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/patients'
      : '/patients/';
    return apiClient.get(endpoint, { params });
  },

  // Get patient by ID
  get: (patientId: string, params?: { purpose?: string }) => {
    // FastAPI: GET /api/internal/patients/{id}
    // Django: GET /patients/{id}
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/patients/${patientId}`
      : `/patients/${patientId}`;
    return apiClient.get(endpoint, { params });
  },

  // Get patient by phone
  getByPhone: (phoneNumber: string, params?: { purpose?: string }) => {
    // FastAPI: GET /api/internal/patients/phone/{phone}
    // Django: GET /patients/phone/{phone}
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/patients/phone/${phoneNumber}`
      : `/patients/phone/${phoneNumber}`;
    return apiClient.get(endpoint, { params });
  },

  // Create patient
  create: (data: any) => {
    // FastAPI: POST /api/internal/patients
    // Django: POST /patients/
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/patients'
      : '/patients/';
    return apiClient.post(endpoint, data);
  },

  // Update patient
  update: (patientId: string, data: any) => {
    // FastAPI: PUT /api/internal/patients/{id}/update
    // Django: PUT /patients/{id}/update
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/patients/${patientId}/update`
      : `/patients/${patientId}/update`;
    return apiClient.put(endpoint, data);
  },

  // Delete patient
  delete: (patientId: string) => {
    // FastAPI: DELETE /api/internal/patients/{id}/delete
    // Django: DELETE /patients/{id}/delete
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/patients/${patientId}/delete`
      : `/patients/${patientId}/delete`;
    return apiClient.delete(endpoint);
  },

  // Check for duplicate patients
  checkDuplicate: (data: {
    first_name: string;
    last_name: string;
    date_of_birth: string;
    gender: string;
    phone_primary?: string;
  }) => {
    // FastAPI: POST /api/internal/patients/check-duplicate/
    // Django: POST /patients/check-duplicate/
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/patients/check-duplicate/'
      : '/patients/check-duplicate/';
    return apiClient.post(endpoint, data);
  },
};

// Prescription API
export const prescriptionApi = {
  // Upload and process prescription with OCR
  upload: (imageFile: File) => {
    // FastAPI: POST /api/internal/prescriptions/upload
    // Django: POST /prescriptions/
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/prescriptions/upload'
      : '/prescriptions/';

    const formData = new FormData();
    formData.append('image', imageFile);

    return apiClient.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 seconds for OCR processing
    });
  },

  // List prescriptions
  list: (params?: {
    status?: string;
    page?: number;
    limit?: number;
  }) => {
    // FastAPI: GET /api/internal/prescriptions
    // Django: GET /prescriptions/
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/prescriptions'
      : '/prescriptions/';
    return apiClient.get(endpoint, { params });
  },

  // Get prescription by ID
  get: (prescriptionId: string) => {
    // FastAPI: GET /api/internal/prescriptions/{id}
    // Django: GET /prescriptions/{id}
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/prescriptions/${prescriptionId}`
      : `/prescriptions/${prescriptionId}`;
    return apiClient.get(endpoint);
  },

  // Get prescriptions for a patient
  getByPatient: (patientId: string) => {
    // FastAPI: GET /api/internal/prescriptions/patient/{id}
    // Django: GET /prescriptions/?patient_id={id}
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/prescriptions/patient/${patientId}`
      : `/prescriptions/?patient_id=${patientId}`;
    return apiClient.get(endpoint);
  },

  // Get prescriptions needing review
  needingReview: (limit: number = 100) => {
    // FastAPI: GET /api/internal/prescriptions/needing-review
    // Django: GET /prescriptions/?status=review
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/prescriptions/needing-review'
      : '/prescriptions/?status=review';
    return apiClient.get(endpoint, { params: { limit } });
  },

  // Get unverified prescriptions
  unverified: (limit: number = 100) => {
    // FastAPI: GET /api/internal/prescriptions/unverified
    // Django: GET /prescriptions/?verified_at__isnull=true
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? '/api/internal/prescriptions/unverified'
      : '/prescriptions/?verified_at__isnull=true';
    return apiClient.get(endpoint, { params: { limit } });
  },

  // Update prescription
  update: (prescriptionId: string, data: any) => {
    // FastAPI: PATCH /api/internal/prescriptions/{id}
    // Django: PATCH /prescriptions/{id}
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/prescriptions/${prescriptionId}`
      : `/prescriptions/${prescriptionId}`;
    return apiClient.patch(endpoint, data);
  },

  // Verify prescription
  verify: (prescriptionId: string, notes?: string) => {
    // FastAPI: POST /api/internal/prescriptions/{id}/verify
    // Django: POST /prescriptions/{id}/verify
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/prescriptions/${prescriptionId}/verify`
      : `/prescriptions/${prescriptionId}/verify`;
    return apiClient.post(endpoint, { notes });
  },

  // Link prescription to patient
  linkToPatient: (prescriptionId: string, patientId: string) => {
    // FastAPI: POST /api/internal/prescriptions/{id}/link-patient/{patient_id}
    // Django: PATCH /prescriptions/{id} with linked_patient
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/prescriptions/${prescriptionId}/link-patient/${patientId}`
      : `/prescriptions/${prescriptionId}`;

    if (BACKEND_CONFIG.isFastAPI) {
      return apiClient.post(endpoint);
    } else {
      return apiClient.patch(endpoint, { linked_patient: patientId });
    }
  },

  // Delete prescription
  delete: (prescriptionId: string) => {
    // FastAPI: DELETE /api/internal/prescriptions/{id}
    // Django: DELETE /prescriptions/{id}
    const endpoint = BACKEND_CONFIG.isFastAPI
      ? `/api/internal/prescriptions/${prescriptionId}`
      : `/prescriptions/${prescriptionId}`;
    return apiClient.delete(endpoint);
  },
};